import React, { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import TableContainer from "Common/TableContainer";
import { useFormik } from "formik";
import "flatpickr/dist/flatpickr.css";
import * as Yup from "yup";
import { addAsset, getAssetsByPaatrimonialStateId } from "helpers/fakebackend_helper";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoader } from "../../../context/LoaderContext";

const Asset = forwardRef(
  (
    {
      onSuccess,
      patrimonialStateId,
    }: { onSuccess?: () => void; patrimonialStateId?: string },
    ref
  ) => {
    const [showAssetForm, setShowAssetForm] = useState(false);
    const [assetList, setAssetList] = useState<any[]>([]);
    const { showLoader, hideLoader } = useLoader();

    const handleAddNewAsset = () => {
      setShowAssetForm(true);
    };

    const handleBackToList = () => {
      setShowAssetForm(false);
    };

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        validation.handleSubmit();
      },
      resetForm: () => {
        validation.resetForm();
        setAssetList([]);
      },
    }));

    /**
     * Fetch Assets by patrimonialStateId
     */
    const fetchAssets = async () => {
      if (!patrimonialStateId) return;

      const params = {patrimonialStateId}

      showLoader();
      try {
        const response = await getAssetsByPaatrimonialStateId(params);
        const assets = response.data._embedded.assets || [];
        setAssetList(assets);
        
        // Notificar al componente padre que los datos han cambiado
        onSuccess?.();
      } catch (error) {
        toast.error("Error al obtener los ingresos: " + error, { autoClose: 2000 });
      } finally {
        hideLoader();
      }
    };

    /**
     * Effect to fetch assets when patrimonialStateId changes
     */
    useEffect(() => {
      fetchAssets();
    }, [patrimonialStateId]);

    const linkifyEntity = (entityName: string, id: number) => {
      return `/${entityName}s/${id}`;
    };

    const validation: any = useFormik({
      enableReinitialize: true,
      initialValues: {
        name: "",
        value: 0,
      },
      validationSchema: Yup.object({
        name: Yup.string().required("Nombre es requerido"),
        value: Yup.number().required("Valor es requerido"),
      }),
      onSubmit: async (values: any) => {
        if (!patrimonialStateId) {
          toast.error("Error: No se ha proporcionado el ID del estado patrimonial");
          return;
        }

        showLoader();
        try {
          const assetData = {
            patrimonialStatus: linkifyEntity("patrimonialStatus", Number(patrimonialStateId)),
            name: values.name,
            value: values.value,
          };

          const response = await addAsset(assetData);

          if (!response || !response.data) {
            toast.error("Error al crear el ingreso");
            return;
          }
          toast.success("Ingreso creado correctamente", { autoClose: 2000 });

          // Refresh the asset list after adding
          await fetchAssets();

          setShowAssetForm(false);
          validation.resetForm();
        } catch (error) {
          toast.error("Error al crear el ingreso: " + error, { autoClose: 2000 });
        } finally {
          hideLoader();
        }
      },
    });

    // Calcular el total de assets para mostrar
    const totalAssets = assetList.reduce((sum, asset) => sum + (asset.value || 0), 0);

    const columns = [
      {
        header: "Nombre",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Valor",
        accessorKey: "value",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: any) => {
          return `Q${row.original.value.toLocaleString()}`;
        },
      },
    ];

    return (
      <React.Fragment>
        {showAssetForm ? (
          <>
            <h5 className="mb-3">Agregar Ingreso</h5>
            <form onSubmit={validation.handleSubmit}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="name" className="inline-block mb-2 text-base font-medium">
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    className="form-input border-slate-200"
                    placeholder="Ingrese nombre"
                    value={validation.values.name}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                  />
                  {validation.touched.name && validation.errors.name ? (
                    <div className="text-red-500">{validation.errors.name}</div>
                  ) : null}
                </div>

                <div>
                  <label htmlFor="value" className="inline-block mb-2 text-base font-medium">
                    Valor <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="value"
                    className="form-input border-slate-200"
                    placeholder="Ingrese valor"
                    value={validation.values.value}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                  />
                  {validation.touched.value && validation.errors.value ? (
                    <div className="text-red-500">{validation.errors.value}</div>
                  ) : null}
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={handleBackToList} className="btn bg-red-500 text-white">
                  Cancelar
                </button>
                <button type="submit" className="btn bg-green-500 text-white">
                  Guardar
                </button>
              </div>
            </form>
          </>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-semibold">Ingresos</h2>
                {assetList.length > 0 && (
                  <p className="text-sm text-green-600 font-medium">
                    Total: Q{totalAssets.toLocaleString()}
                  </p>
                )}
              </div>
              <button
                className="btn bg-blue-500 text-white"
                onClick={handleAddNewAsset}
              >
                Agregar Ingreso
              </button>
            </div>

            {assetList.length === 0 ? (
              <p className="text-gray-500">No se han agregado ingresos aún.</p>
            ) : (
              <TableContainer
                isPagination={true}
                isGlobalFilter={false}
                columns={columns}
                data={assetList}
                customPageSize={5}
                divclassName="my-2 col-span-12 overflow-x-auto lg:col-span-12"
                tableclassName="hover group dataTable w-full text-sm align-middle whitespace-nowrap no-footer"
                theadclassName="border-b border-slate-200 dark:border-zink-500"
                trclassName="group-[.stripe]:even:bg-slate-50 group-[.stripe]:dark:even:bg-zink-600 transition-all duration-150 ease-linear group-[.hover]:hover:bg-slate-50 dark:group-[.hover]:hover:bg-zink-600 [&.selected]:bg-custom-500 dark:[&.selected]:bg-custom-500 [&.selected]:text-custom-50 dark:[&.selected]:text-custom-50"
                thclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500 sorting px-3 py-4 text-slate-900 bg-slate-200/50 font-semibold text-left dark:text-zink-50 dark:bg-zink-600 dark:group-[.bordered]:border-zink-500"
                tdclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500"
                PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
              />
            )}
          </div>
        )}
      </React.Fragment>
    );
  }
);

export default Asset;